import Head from 'next/head';
import '../styles/reset.scss';
import '../styles/globals.scss';
import 'katex/dist/katex.min.css';
import 'public/static/fonts/fonts.css';
import 'react-photo-view/dist/react-photo-view.css';
import 'react-toastify/dist/ReactToastify.css';
import 'react-loading-skeleton/dist/skeleton.css';
import 'react-phone-input-2/lib/style.css';
import React, { useEffect, useState } from 'react';
import { RecoilRoot } from 'recoil';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useRouter } from 'next/router';
import { useEventPropertiesStore } from '@store/common';
import { Slide } from 'react-toastify';
import { configureScope } from '@sentry/nextjs';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import { queryClientConfig } from 'queries/core/config';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { appWithTranslation } from 'next-i18next';
import nextI18nConfig from '../next-i18next.config';
import { CustomToastContainer } from '@styles/common/toastStyle';
import { useUserInfoQuery } from '@apis/query/user';
import 'swiper/css';
import * as amplitude from '@amplitude/analytics-browser';
import { UserType } from '@asset/enum/UserEnum';
import { useCheckIsIncorrectBuildIdQuery } from '@apis/query/core';
import NotifyUpdateToast from '@components/common/toast/NotifyUpdateToast';
import { GlobalStyle } from '@styles/globalsStyles';
import { createGlobalStyle } from 'styled-components';
import { Settings } from 'luxon';
import Gnb from '@components/common/nav/Gnb';
import { useWindowSize } from '@public/hooks/useWindowSize';
import '@sendbird/uikit-react/dist/index.css';
import SendbirdWrapper from '@components/chat/ChatRoom';
import { useChatPopupStore, useChatStore, useNotificationStore } from '@store/chat';
import { getSendbirdUserInfo } from '@apis/api/chat';
import _ from 'lodash';
import QuestionChatDetailModal from '@components/chat/QuestionChatDetailModal';
import { getIsVisibleChatButton, registerFCMToSendbird, withSendbird } from '@asset/function/chat';
import { getDeviceToken } from '@asset/function/notification';
import { registerPushToken, TokenType, unregisterPushToken } from '@apis/api/user';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { SENDBIRD_NOTIFICATION_BROADCAST_CHANNEL_NAME } from 'constants/chat';
import { getExternalBrowserRedirectPageUrlObject } from 'routes/account';
import { useBrowser, useDeviceType } from 'hook/checkUserAgent';
import NoticeBanner from '@components/common/NoticeBanner';
import { IconCircleCheckFilled, IconCircleXFilled, IconInfoCircleFilled } from '@tabler/icons-react';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const AppJsxGlobalStyle = createGlobalStyle(["body{> div{display:flex;flex-direction:column;}}"]);
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);
dayjs.extend(utc);
dayjs.extend(timezone);
function CheckLoginState({
  children
}) {
  const {
    data: user
  } = useUserInfoQuery();
  const {
    data: isIncorrectBuildId
  } = useCheckIsIncorrectBuildIdQuery({
    refetchInterval: 60000
  });
  const {
    setUnreadCount
  } = useChatStore();
  const {
    notificationPermission,
    setNotificationPermission
  } = useNotificationStore();
  const router = useRouter();
  const [showBrowserNoticeBanner, setShowBrowserNoticeBanner] = useState(false);
  const [showTurnOnChatNotificationNoticeBanner, setShowTurnOnChatNotificationNoticeBanner] = useState(false);
  const {
    isMobileView
  } = useWindowSize();
  const {
    isChrome,
    isSafari,
    isSamsungBrowser
  } = useBrowser();
  const {
    isIpad,
    isTablet
  } = useDeviceType();
  const isOtherTablet = !isIpad && isTablet;
  const isErrorPage = router.pathname === '/_error' || router.pathname === '/404' || router.pathname === '/500';
  const isAccountPage = router.pathname.startsWith('/account');
  const {
    data: sendbirdUserInfo
  } = useQuery(['GET_SENDBIRD_USER_INFO', user?.userId], () => getSendbirdUserInfo(user?.userId), {
    enabled: !_.isNil(user?.userId)
  });
  const sendbirdUserId = sendbirdUserInfo?.userId;
  const sendbirdAccessToken = sendbirdUserInfo?.accessToken;
  useEffect(() => {
    amplitude.init(process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY, user ? String(user.userId) : null, {
      minIdLength: 1,
      defaultTracking: {
        pageViews: true
      }
    });
    const identifyEvent = new amplitude.Identify();
    if (user) {
      identifyEvent.set('userId', user.userId);
      identifyEvent.set('timezone', user?.timezone);
      identifyEvent.set('language', user?.language);
      identifyEvent.set(user.type === UserType.TEACHER ? 'teacherId' : 'studentId', user.id);
      configureScope(scope => {
        scope.setUser({
          type: user.type,
          id: user.id?.toString(),
          name: user.name,
          timezone: user.timezone,
          language: user.language
        });
      });
      if (user.type === UserType.STUDENT) Settings.defaultZone = user.timezone;
    }
    amplitude.identify(identifyEvent);
    return () => {
      configureScope(scope => scope.clear());
    };
  }, [user]);
  useEffect(() => {
    if (!isMobileView && user?.type !== UserType.PARENT && (isIpad && !isSafari || isOtherTablet && !isChrome || isSamsungBrowser)) {
      setShowBrowserNoticeBanner(true);
    }
  }, [user, isIpad, isOtherTablet, isChrome, isSafari, isSamsungBrowser, isMobileView]);
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
    if (user?.type === UserType.STUDENT && !_.isNil(sendbirdUserInfo?.currentClassChannelUrl)) {
      setShowTurnOnChatNotificationNoticeBanner(notificationPermission !== 'granted');
    } else {
      setShowTurnOnChatNotificationNoticeBanner(false);
    }
  }, [user, sendbirdUserInfo, notificationPermission]);
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/kakaotalk/i.test(userAgent)) {
      router.push(getExternalBrowserRedirectPageUrlObject({
        redirectUrl: window.location.href
      }));
    }
  }, []);
  const setUpSendbird = async (sendbirdUserId, sendbirdAccessToken) => {
    const currentToken = await getDeviceToken();
    await withSendbird(sendbirdUserId, sendbirdAccessToken, async sb => {
      if (!_.isNil(sb.groupChannel)) {
        const unread = await sb.groupChannel.getTotalUnreadMessageCount();
        setUnreadCount(unread);
      }
      if (currentToken) {
        await registerFCMToSendbird(sb, currentToken);
      }
    });
  };
  useEffect(() => {
    if (sendbirdUserId && sendbirdAccessToken) {
      setUpSendbird(sendbirdUserId, sendbirdAccessToken);
    }
  }, [user, sendbirdUserId, sendbirdAccessToken]);
  useEffect(() => {
    if (user) {
      getDeviceToken().then(async token => {
        if (token) {
          await unregisterPushToken(user.userId);
          registerPushToken(user.userId, TokenType.FCM, token);
        }
      });
    }
  }, [user]);
  const isVisibleChatButton = getIsVisibleChatButton(sendbirdUserInfo, user?.type, user?.language);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isIncorrectBuildId && /*#__PURE__*/_jsx(NotifyUpdateToast, {}), /*#__PURE__*/_jsxs("section", {
      className: "flex flex-col",
      style: {
        height: '100%'
      },
      children: [showBrowserNoticeBanner && /*#__PURE__*/_jsx(NoticeBanner, {
        content: `올마이티캠퍼스는 ${isIpad ? 'Safari' : 'Chrome'} 환경에 최적화 되어있습니다. ${isIpad ? 'Safari' : 'Chrome'} 브라우저로 접속해 주세요.`,
        onClose: () => setShowBrowserNoticeBanner(false)
      }), showTurnOnChatNotificationNoticeBanner && /*#__PURE__*/_jsx(NoticeBanner, {
        content: /*#__PURE__*/_jsxs(_Fragment, {
          children: ["\uCC44\uD305 \uC54C\uB9BC\uC774 \uAEBC\uC838\uC788\uC5B4\uC694. \uC6D0\uD65C\uD55C \uCC44\uD305\uC744 \uC704\uD574 \uCC44\uD305 \uC54C\uB9BC\uC744 \uD5C8\uC6A9\uD574 \uC8FC\uC138\uC694.", /*#__PURE__*/_jsx("button", {
            className: "text-my-blue-50 text-label-lg-700",
            onClick: () => window.open('https://almighty-campus.notion.site/19668fac541a80aba520c2112ea513a7'),
            children: "\uD5C8\uC6A9\uBC29\uBC95 \uC54C\uC544\uBCF4\uAE30"
          })]
        }),
        onClose: () => setShowTurnOnChatNotificationNoticeBanner(false)
      }), isAccountPage || isErrorPage || router.pathname.includes('study-report') || router.pathname.includes('problem-solving') || router.pathname.includes('/feedback/detail') || isMobileView ? /*#__PURE__*/_jsx(_Fragment, {}) : /*#__PURE__*/_jsx(Gnb, {
        isVisibleChatButton: isVisibleChatButton
      }), /*#__PURE__*/_jsx("div", {
        className: "relative flex-1 overflow-auto",
        children: children
      })]
    })]
  });
}
function MainComponent({
  Component,
  pageProps
}) {
  const router = useRouter();
  if (router.pathname.startsWith('/account/signup/complete')) {
    return /*#__PURE__*/_jsx(Component, {
      ...pageProps
    });
  }
  return /*#__PURE__*/_jsxs(CheckLoginState, {
    children: [/*#__PURE__*/_jsx(SendbirdWrapper, {}), /*#__PURE__*/_jsx(QuestionChatDetailModal, {}), /*#__PURE__*/_jsx(Component, {
      ...pageProps
    })]
  });
}
function MyApp({
  Component,
  pageProps
}) {
  const [queryClient] = useState(() => {
    return new QueryClient(queryClientConfig);
  });
  const router = useRouter();
  const {
    setProperties
  } = useEventPropertiesStore();
  const {
    selectedChannel,
    setUnreadCount,
    setClickedChatNotification
  } = useChatStore();
  const {
    setIsChatPopupOpen
  } = useChatPopupStore();
  useEffect(() => {
    const broadcastChannel = new BroadcastChannel(SENDBIRD_NOTIFICATION_BROADCAST_CHANNEL_NAME);
    const onMessage = event => {
      if (event.data.action === 'push') {
        const pushChannelUrl = event.data.data.sendbird?.channel?.channel_url;
        if (selectedChannel?.url && selectedChannel.url !== pushChannelUrl) {
          setUnreadCount(event.data.data.sendbird?.channel?.channel_unread_message_count);
        }
      }
    };
    broadcastChannel?.addEventListener('message', onMessage);
    return () => {
      broadcastChannel?.removeEventListener('message', onMessage);
      broadcastChannel?.close();
    };
  }, [selectedChannel?.url]);
  useEffect(() => {
    const onMessage = event => {
      if (event.data.action === 'notificationClick') {
        const clickedChannelUrl = event.data.data.sendbird?.channel?.channel_url || event.data.data.content?.student?.sendbirdChannelUrl;
        if (!_.isNil(clickedChannelUrl)) {
          setClickedChatNotification({
            channelUrl: clickedChannelUrl
          });
          setIsChatPopupOpen(true);
        }
      }
    };
    navigator?.serviceWorker?.addEventListener('message', onMessage);
    return () => {
      navigator?.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, []);
  useEffect(() => {
    const chartTooltip = document.querySelector('div#chartjs-tooltip');
    if (chartTooltip) {
      chartTooltip.remove();
    }
    setProperties({
      url: typeof window !== 'undefined' ? window.location.href : '',
      path: router.asPath
    });
  }, [router.asPath]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(GlobalStyle, {}), /*#__PURE__*/_jsx(QueryClientProvider, {
      client: queryClient,
      children: /*#__PURE__*/_jsx(RecoilRoot, {
        children: /*#__PURE__*/_jsxs(GoogleOAuthProvider, {
          clientId: process.env.NEXT_PUBLIC_CLIENT_ID,
          children: [/*#__PURE__*/_jsxs(Head, {
            children: [/*#__PURE__*/_jsx("meta", {
              name: "robots",
              content: "noindex"
            }), /*#__PURE__*/_jsx("meta", {
              name: "viewport",
              content: "width=device-width, initial-scale=1.0, viewport-fit=cover"
            }), /*#__PURE__*/_jsx("link", {
              rel: "manifest",
              href: "/manifest.json"
            }), /*#__PURE__*/_jsx("link", {
              rel: "shortcut icon",
              href: "/common/logo/favicon.svg",
              type: "image/x-icon"
            })]
          }), /*#__PURE__*/_jsxs("main", {
            children: [/*#__PURE__*/_jsx(MainComponent, {
              Component: Component,
              pageProps: pageProps
            }), /*#__PURE__*/_jsx(CustomToastContainer, {
              position: "top-center",
              autoClose: 2500,
              hideProgressBar: true,
              newestOnTop: false,
              closeOnClick: true,
              rtl: false,
              pauseOnFocusLoss: false,
              draggable: true,
              pauseOnHover: true,
              theme: "light",
              transition: Slide,
              icon: ({
                type
              }) => {
                if (type === 'success') {
                  return /*#__PURE__*/_jsx(IconCircleCheckFilled, {
                    size: 24,
                    color: "var(--green-50)"
                  });
                } else if (type === 'info') {
                  return /*#__PURE__*/_jsx(IconCircleXFilled, {
                    size: 24,
                    color: "var(--red-50)"
                  });
                } else if (type === 'error') {
                  return /*#__PURE__*/_jsx(IconInfoCircleFilled, {
                    size: 24,
                    color: "var(--gray-30)"
                  });
                }
              }
            })]
          }), /*#__PURE__*/_jsx(AppJsxGlobalStyle, {})]
        })
      })
    })]
  });
}
export default appWithTranslation(MyApp, nextI18nConfig);